'use client';

import { useTranslations } from 'next-intl';
import {
  removeAtIndex,
  toBusinessProfileEntity,
  toEntityFormValues,
  upsertAtIndex,
  type BusinessProfileEntity,
  type BusinessProfileEntityFormValues,
} from './business-profile.model';
import {
  AddEntityDialog,
  type BusinessProfileEntityNamespace,
} from './AddEntityDialog';
import { ProfileListCard } from './ProfileListCard';
import { useProfileListDialog } from './useProfileListDialog';

export type EntityListEditorProps = {
  values: BusinessProfileEntity[];
  isReadOnly: boolean;
  isSaving: boolean;
  onSave: (values: BusinessProfileEntity[]) => Promise<boolean>;
  translationsNamespace: BusinessProfileEntityNamespace;
};

export function EntityListEditor({
  values,
  isReadOnly,
  isSaving,
  onSave,
  translationsNamespace,
}: EntityListEditorProps) {
  const t = useTranslations(translationsNamespace);
  const dialog = useProfileListDialog();
  const initialValues = toEntityFormValues(
    dialog.editIndex === null ? undefined : values[dialog.editIndex],
  );

  const handleSave = async (formValues: BusinessProfileEntityFormValues) => {
    const saved = await onSave(
      upsertAtIndex({
        values,
        value: toBusinessProfileEntity(formValues),
        editIndex: dialog.editIndex,
      }),
    );

    if (saved) {
      dialog.close();
    }
  };

  return (
    <ProfileListCard
      title={t('title')}
      tooltip={t('tooltip')}
      items={values.map((value, index) => ({
        key: `${index}-${value.name}`,
        label: value.name,
        value: value.location ?? t('noLocation'),
      }))}
      emptyLabel={t('emptyStateLabel')}
      emptyValue={t('emptyStateValue')}
      addLabel={t('add')}
      isReadOnly={isReadOnly}
      isSaving={isSaving}
      onAdd={dialog.openCreate}
      onEdit={dialog.openEdit}
      onDelete={(index) => void onSave(removeAtIndex({ values, index }))}
    >
      <AddEntityDialog
        open={dialog.isOpen}
        isCreate={dialog.isCreate}
        editIndex={dialog.editIndex}
        existingNames={values.map((value) => value.name)}
        initialValues={initialValues}
        onOpenChange={(open) => {
          if (!open) {
            dialog.close();
          }
        }}
        onSave={handleSave}
        isSaving={isSaving}
        translationsNamespace={translationsNamespace}
      />
    </ProfileListCard>
  );
}
